import React, { useEffect, useRef, useState } from "react";
import * as pdfjsLib from "pdfjs-dist/legacy/build/pdf.js";
import "pdfjs-dist/legacy/build/pdf.worker.entry.js";
import { getBlob, ref } from "firebase/storage";
import { useReactToPrint } from "react-to-print";
import { storage } from "../Config/firebase";
import RelatorioCalibracaoPrensaPrint from "../CalibracaoPrensa/RelatorioCalibracaoPrensaPrint";
import {
  calcularBlocos,
  calcularBlocosAbsorcao,
  calcularIdadeDias,
  calcularResumo,
  criarBlocos,
  criarBlocosAbsorcao,
  formatarDataBR,
  numeroBR,
} from "./blocoCeramicoCalculos";
import "./RelatorioRupturaBlocoCeramicoPrint.css";
import LogoRelatorio from "../Components/LogoRelatorio/LogoRelatorio";
import useNomeArquivoPdf from "../Utils/useNomeArquivoPdf";

function estilosImpressaoCeramico() {
  const prefixos = [".relatorio-bloco-ceramico-print", ".relatorio-calibracao-print"];
  const extrair = (regras) => Array.from(regras).map((regra) => {
    if (regra.type === CSSRule.STYLE_RULE) {
      const seletores = regra.selectorText.split(",").map((seletor) => seletor.trim())
        .filter((seletor) => prefixos.some((prefixo) => seletor.startsWith(prefixo)));
      return seletores.length ? seletores.join(", ") + " {" + regra.style.cssText + "}" : "";
    }
    if (regra.type === CSSRule.MEDIA_RULE) {
      const internas = extrair(regra.cssRules);
      return internas ? "@media " + regra.conditionText + " {" + internas + "}" : "";
    }
    return "";
  }).join("\n");

  const estilos = Array.from(document.styleSheets).map((folha) => {
    try {
      return extrair(folha.cssRules);
    } catch {
      return "";
    }
  }).join("\n");

  return "@page relatorioBlocoCeramico { size: A4 portrait; margin: 0; }\n"
    + "html, body { margin: 0; padding: 0; }\n" + estilos;
}

function imprimirEmRetrato() {
  const styleId = "relatorio-bloco-ceramico-print-orientacao";
  document.getElementById(styleId)?.remove();

  const style = document.createElement("style");
  style.id = styleId;
  style.media = "print";
  style.textContent = `
    @page relatorioBlocoCeramico {
      size: A4 portrait;
      margin: 0;
    }
  `;
  document.head.appendChild(style);
  window.print();
}

function valor(valorCampo, fallback = "-") {
  return valorCampo || fallback;
}

function valorTabela(valorCampo, casas = 1, fallback = "-") {
  const formatado = numeroBR(valorCampo, casas);
  return formatado || fallback;
}

function diferencaNominal(nominal, valorCampo) {
  const numero = Number(String(valorCampo || "").replace(",", "."));
  if (!Number.isFinite(numero) || numero <= 0) return "";
  return nominal - numero;
}

function obterDimensoesNominais(dimensoesDeclaradas) {
  const numeros = String(dimensoesDeclaradas || "")
    .match(/\d+(?:[,.]\d+)?/g)
    ?.map((item) => Number(item.replace(",", ".")))
    .filter((item) => Number.isFinite(item));

  if (numeros?.length >= 3) {
    return {
      largura: numeros[0],
      altura: numeros[1],
      comprimento: numeros[2],
    };
  }

  return { largura: 90, comprimento: 240, altura: 140 };
}

function dividir(lista, quantidade) {
  const grupos = [];
  for (let i = 0; i < lista.length; i += quantidade) {
    grupos.push(lista.slice(i, i + quantidade));
  }
  return grupos;
}

function Cabecalho({ ensaio, dataRelatorio, tipoSoloCimento = false }) {
  return (
    <>
      <table className="cabecalho-bloco">
        <tbody>
          <tr>
            <td rowSpan="3" className="logo-cell">
              <LogoRelatorio alt="Logo" className="logo"  />
            </td>
            <td rowSpan="3" className="titulo-principal">
              SISTEMA DE GESTÃO DA QUALIDADE
              <br />
              {tipoSoloCimento
                ? "Relatório de ensaio - Tijolo Solo Cimento - resistência à compressão e da absorção de água."
                : <><span>Relatório de ensaio - Análise dimensional, determinação da resistência à</span><br /><span>compressão e da absorção de água.</span></>}
            </td>
            <td>{tipoSoloCimento ? "RQ 2.3.6" : "RQ 2.3.3"}</td>
          </tr>
          <tr>
            <td>REVISÃO 00</td>
          </tr>
          <tr>
            <td>{tipoSoloCimento ? "08/04/2026" : "20/09/2025"}</td>
          </tr>
        </tbody>
      </table>
      <div className="linha-info">
        <span>
          <strong>DATA</strong>   {formatarDataBR(dataRelatorio)}
        </span>
        <span>
          <strong>CÓDIGO:</strong> {valor(ensaio?.codigo, tipoSoloCimento ? "RE-SC-000/2026" : "RE-BLC-000/2025")}
        </span>
      </div>
    </>
  );
}

function LinhaFinal() {
  return (
    <div className="rodape-restrito">
      <div className="linha-final" aria-hidden="true" />
      <div className="texto-rodape-restrito">
        DOCUMENTO PARA USO RESTRITO. REPRODUÇÃO/CÓPIA PROIBIDA SEM AUTORIZAÇÃO DA EMPRESA
      </div>
    </div>
  );
}

function RelatorioFotografico({ ensaio, fotos = [], tipoSoloCimento = false }) {
  if (!fotos.length) return null;

  return dividir(fotos, 8).map((paginaFotos, paginaIndex) => (
    <div
      className={`pagina pagina-fotografica${tipoSoloCimento ? " pagina-solo-cimento" : ""}`}
      key={`fotos-${paginaIndex}`}
    >
      <Cabecalho ensaio={ensaio} dataRelatorio={ensaio?.dataRelatorio} tipoSoloCimento={tipoSoloCimento} />
      {tipoSoloCimento && (
        <div className="dados-cliente dados-cliente-solo-cimento">
          <div><strong>CLIENTE:</strong> {valor(ensaio?.cliente)}</div>
          <div><strong>ENDEREÇO:</strong> {valor(ensaio?.endereco)}</div>
        </div>
      )}
      <div className="secao">RELATÓRIO FOTOGRÁFICO</div>
      <div className="grade-fotos">
        {dividir(paginaFotos, 2).map((linha, linhaIndex) => (
          <div className="linha-fotos-bloco" key={`linha-${paginaIndex}-${linhaIndex}`}>
            <div className="linha-fotos">
              {linha.map((foto, index) => {
                const numeroFoto = paginaIndex * 8 + linhaIndex * 2 + index + 1;
                return (
                  <div className="foto-item" key={foto.id || foto.url || numeroFoto}>
                    <div className="foto-imagem">
                      <img src={foto.url} alt={foto.legenda || `Foto ${numeroFoto}`} />
                    </div>
                    <div className="foto-legenda">
                      {foto.legenda || `FOTO ${String(numeroFoto).padStart(2, "0")}`}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="linha-azul-fotos" />
          </div>
        ))}
      </div>
      <LinhaFinal />
    </div>
  ));
}

function RelatorioSoloCimento({ ensaio, blocos, blocosAbsorcao, resumo, fotosRelatorio, onVoltar }) {
  const dataFabricacao = ensaio.dataFabricacaoNaoInformada
    ? "Não informado"
    : formatarDataBR(ensaio.dataFabricacao);
  const idadeCalculada = ensaio.dataFabricacaoNaoInformada
    ? "-"
    : calcularIdadeDias(ensaio.dataFabricacao, ensaio.dataEnsaio) || "-";

  return (
    <div className="relatorio-bloco-ceramico-print relatorio-solo-cimento-print">
      {onVoltar && (
        <div className="acoes-print">
          <button className="btn-voltar" onClick={onVoltar}>Voltar</button>
          <button className="btn-imprimir" onClick={imprimirEmRetrato}>Imprimir</button>
        </div>
      )}

      <div className="pagina pagina-solo-cimento">
        <Cabecalho ensaio={ensaio} dataRelatorio={ensaio.dataRelatorio} tipoSoloCimento />

        <div className="dados-cliente dados-cliente-solo-cimento">
          <div><strong>CLIENTE:</strong> {valor(ensaio.cliente)}</div>
          <div><strong>ENDEREÇO:</strong> {valor(ensaio.endereco)}</div>
        </div>

        <div className="secao">1. OBJETIVO DO ENSAIO</div>
        <div className="texto">
          Análise dimensional, determinação da resistência à compressão e da absorção de água em tijolos de solo cimento.
        </div>

        <div className="secao">2. IDENTIFICAÇÃO DA AMOSTRA</div>
        <table className="tabela-info">
          <tbody>
            <tr><td>Procedência:</td><td>{valor(ensaio.procedencia || ensaio.cliente)}</td></tr>
            <tr><td>Tijolo:</td><td>{valor(ensaio.blocoDescricao)}</td></tr>
            <tr><td>Lote:</td><td>{valor(ensaio.lote)}</td></tr>
            <tr><td>Dimensões (Comp x Larg x Alt) mm:</td><td>{valor(ensaio.dimensoesDeclaradas)}</td></tr>
            <tr><td>Resistência esperada:</td><td>{valor(ensaio.resistenciaEsperada)}</td></tr>
          </tbody>
        </table>

        <div className="secao">3. LOCAL DA APLICAÇÃO</div>
        <div className="texto">{valor(ensaio.localAplicacao || ensaio.obra)}</div>

        <div className="secao">4. METODOLOGIA</div>
        <div className="texto metodologia-solo-cimento">
          Os ensaios seguiram as prescrições das normas da ABNT:<br />
          NBR 8491:2013 - Tijolo de solo-cimento — Requisitos;<br />
          NBR 8492:2013 - Tijolo de solo-cimento — Análise dimensional, determinação da resistência à compressão e da absorção de água — Método de ensaio;<br />
          NBR 6136:2016 - Blocos vazados de concreto simples para alvenaria - Requisitos.
        </div>

        <div className="secao">5. DADOS DOS ENSAIOS</div>
        <div className="subsecao">5.1. Ensaio de compressão e análise dimensional</div>
        <div className="texto texto-dados-solo-cimento">
          Foram ensaiados à compressão axial: <strong>{String(blocos.length).padStart(2, "0")}</strong> tijolos solo cimento.
          <span> recebimento: <strong>{formatarDataBR(ensaio.dataRecebimento)}</strong></span><br />
          Os blocos foram capeados com pasta de cimento nas faces de aplicação de carga.<br />
          Data de fabricação: <strong>{dataFabricacao}</strong> &nbsp;&nbsp;
          Data do ensaio: <strong>{formatarDataBR(ensaio.dataEnsaio)}</strong> &nbsp;&nbsp;
          Idade declarada: <strong>{idadeCalculada}</strong>
        </div>

        <div className="quadro-titulo">Quadro 1 - Resultados de Resistência à Compressão Axial e análise dimensional</div>
        <table className="tabela-resultados tabela-planilha quadro-solo-cimento">
          <thead>
            <tr>
              <th rowSpan="3">BLOCO<br />Nº</th>
              <th rowSpan="3">Massa<br />(g)</th>
              <th colSpan="9">DIMENSÕES (mm)</th>
              <th colSpan="2">RUPTURA</th>
            </tr>
            <tr>
              <th colSpan="2">Largura (L)</th>
              <th colSpan="2">Comprim. (C)</th>
              <th colSpan="2">Altura (H)</th>
              <th colSpan="3">Média</th>
              <th rowSpan="2">Área<br />(mm²)</th>
              <th rowSpan="2">Tensão<br />(MPa)</th>
            </tr>
            <tr>
              <th>1ª Det.</th><th>2ª Det.</th>
              <th>1ª Det.</th><th>2ª Det.</th>
              <th>1ª Det.</th><th>2ª Det.</th>
              <th>L</th><th>C</th><th>H</th>
            </tr>
          </thead>
          <tbody>
            {blocos.map((item, index) => (
              <tr key={index}>
                <td>{item.bloco || String(index + 1).padStart(2, "0")}</td>
                <td>{valorTabela(item.massa, 1)}</td>
                <td>{valorTabela(item.largura1, 1)}</td><td>{valorTabela(item.largura2, 1)}</td>
                <td>{valorTabela(item.comprimento1, 1)}</td><td>{valorTabela(item.comprimento2, 1)}</td>
                <td>{valorTabela(item.altura1, 1)}</td><td>{valorTabela(item.altura2, 1)}</td>
                <td>{valorTabela(item.larguraMedia, 1)}</td>
                <td>{valorTabela(item.comprimentoMedia, 1)}</td>
                <td>{valorTabela(item.alturaMedia, 1)}</td>
                <td>{valorTabela(item.area, 0)}</td>
                <td><strong>{valorTabela(item.tensaoRupturaMpa, 2)}</strong></td>
              </tr>
            ))}
            <tr className="linha-resumo-solo-cimento">
              <td colSpan="10" rowSpan="2"></td>
              <td colSpan="2">Valor mínimo</td>
              <td><strong>{valorTabela(resumo.resistenciaMinima, 1)}</strong></td>
            </tr>
            <tr className="linha-resumo-solo-cimento">
              <td colSpan="2">Resistência média</td>
              <td><strong>{valorTabela(resumo.resistenciaMedia, 1)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className="subsecao">5.2. Ensaio de absorção de água</div>
        <div className="quadro-titulo">Quadro 3 - Resultados ensaio de Absorção</div>
        <table className="tabela-resultados tabela-absorcao-solo-cimento">
          <thead>
            <tr>
              <th>BLOCO</th><th>Seco na estufa</th><th>Massa úmida (Mu)</th><th>Absorção (%)</th>
              <th>IMAGEM REPRESENTATIVA DO ENSAIO</th>
            </tr>
          </thead>
          <tbody>
            {blocosAbsorcao.map((item, index) => (
              <tr key={index}>
                <td>{item.bloco || String(index + 1).padStart(2, "0")}</td>
                <td>{valorTabela(item.massaSeca, 1)}</td>
                <td>{valorTabela(item.massaUmida, 1)}</td>
                <td>{valorTabela(item.absorcao, 1)}</td>
                {index === 0 && (
                  <td rowSpan={Math.max(blocosAbsorcao.length, 1) + 1} className="celula-fotos-absorcao">
                    <div className="fotos-absorcao fotos-absorcao-solo-cimento">
                      {[0, 1, 2].map((fotoIndex) => {
                        const foto = ensaio.absorcaoFotos?.[fotoIndex];
                        return foto?.url
                          ? <img key={fotoIndex} src={foto.url} alt={foto.legenda || `Absorção ${fotoIndex + 1}`} />
                          : <div key={fotoIndex} className="foto-absorcao-vazia" />;
                      })}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            <tr className="linha-absorcao-media">
              <td colSpan="3">Absorção média (%)</td>
              <td><strong>{valorTabela(resumo.absorcaoMedia, 1)}</strong></td>
            </tr>
          </tbody>
        </table>
        <LinhaFinal />
      </div>
      <RelatorioFotografico ensaio={ensaio} fotos={fotosRelatorio} tipoSoloCimento />
      {ensaio.prensaRuptura && (
        <div className="certificado-prensa-anexo">
          <RelatorioCalibracaoPrensaPrint dados={ensaio.prensaRuptura} />
        </div>
      )}
    </div>
  );
}

function PaginasCertificadoCelulaCarga({ ensaio, dataRelatorio, caminho, url, onPronto }) {
  const [paginas, setPaginas] = useState([]);
  const [erro, setErro] = useState(false);

  useEffect(() => {
    let ativo = true;
    async function carregar() {
      setPaginas([]);
      setErro(false);
      let blob;
      if (caminho) {
        try {
          blob = await getBlob(ref(storage, caminho));
        } catch (erroStorage) {
          if (!url) throw erroStorage;
        }
      }
      if (!blob) {
        const resposta = await fetch(url);
        if (!resposta.ok) throw new Error(`HTTP ${resposta.status}`);
        blob = await resposta.blob();
      }
      const pdf = await pdfjsLib.getDocument({ data: await blob.arrayBuffer() }).promise;
      const imagens = [];
      try {
        for (let numero = 1; numero <= pdf.numPages; numero += 1) {
          const pagina = await pdf.getPage(numero);
          const viewport = pagina.getViewport({ scale: 1.8 });
          const canvas = document.createElement("canvas");
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          await pagina.render({ canvasContext: canvas.getContext("2d"), viewport }).promise;
          imagens.push(canvas.toDataURL("image/jpeg", 0.92));
          pagina.cleanup();
        }
      } finally {
        await pdf.destroy();
      }
      if (ativo) setPaginas(imagens);
    }
    carregar().catch((erroCarregamento) => {
      console.error("Erro ao carregar certificado da célula de carga:", erroCarregamento);
      if (ativo) setErro(true);
    });
    return () => { ativo = false; };
  }, [caminho, url]);

  useEffect(() => {
    if (paginas.length > 0 || erro) onPronto();
  }, [paginas, erro, onPronto]);

  return (
    <div className="certificado-prensa-anexo">
      {(paginas.length ? paginas : [null]).map((imagem, indice) => (
        <div className="pagina pagina-certificado-celula" key={indice}>
          <Cabecalho ensaio={ensaio} dataRelatorio={dataRelatorio} />
          <div className="secao">ANEXO 2 - CERTIFICADO DE CALIBRAÇÃO - CÉLULA DE CARGA</div>
          {imagem ? (
            <div className="certificado-celula-conteudo">
              <img src={imagem} alt={`Certificado de calibração - página ${indice + 1}`} />
            </div>
          ) : (
            <div className="texto">{erro ? "Não foi possível carregar o certificado de calibração." : "Carregando certificado de calibração..."}</div>
          )}
        </div>
      ))}
    </div>
  );
}

export default function RelatorioRupturaBlocoCeramicoPrint({
  ensaio,
  fotosRelatorio = [],
  onVoltar,
  autoPrint = false,
  tipoSoloCimento = false,
}) {
  const jaImprimiuRef = useRef(false);
  const relatorioCeramicoRef = useRef(null);
  const [certificadoPronto, setCertificadoPronto] = useState(false);
  useNomeArquivoPdf(tipoSoloCimento ? ensaio?.codigo : null);
  const imprimirRelatorioCeramico = useReactToPrint({
    contentRef: relatorioCeramicoRef,
    documentTitle: ensaio?.codigo || "Relatorio-bloco-ceramico",
    ignoreGlobalStyles: true,
    pageStyle: estilosImpressaoCeramico(),
  });
  const opcoesCalculo = { dividirComprimentoArea: tipoSoloCimento };
  const blocos = calcularBlocos(
    tipoSoloCimento ? ensaio?.blocos || [] : ensaio?.blocos?.length ? ensaio.blocos : criarBlocos(13),
    opcoesCalculo
  );
  const blocosAbsorcao = calcularBlocosAbsorcao(
    tipoSoloCimento
      ? ensaio?.blocosAbsorcao || []
      : ensaio?.blocosAbsorcao?.length ? ensaio.blocosAbsorcao : criarBlocosAbsorcao(6)
  );
  const resumo = calcularResumo(blocos, blocosAbsorcao, opcoesCalculo);
  const dimensoesNominais = obterDimensoesNominais(ensaio?.dimensoesDeclaradas);
  const dataRelatorio = ensaio?.dataRelatorio;
  const idade = ensaio?.dataFabricacaoNaoInformada
    ? "-"
    : calcularIdadeDias(ensaio?.dataFabricacao, ensaio?.dataEnsaio);
  const dataFabricacao = ensaio?.dataFabricacaoNaoInformada
    ? "-"
    : formatarDataBR(ensaio?.dataFabricacao);
  const documentoCertificado = ensaio?.celulaCargaRuptura?.certificadoCalibracao;
  const caminhoCertificado = documentoCertificado?.storagePathAtual || "";
  const certificadoCelulaCarga =
  documentoCertificado?.urlCertificado ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.url ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.arquivo ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.arquivoUrl ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.downloadURL ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.fileUrl ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.anexoUrl ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.storageUrl ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.file?.url ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.arquivoAtual?.url ||
  ensaio?.celulaCargaRuptura?.certificadoCalibracao?.arquivoAtualUrl ||
  ensaio?.celulaCargaRuptura?.certificado?.url ||
  ensaio?.celulaCargaRuptura?.certificado?.arquivo ||
  ensaio?.celulaCargaRuptura?.imagemCertificadoCalibracao?.url ||
  ensaio?.celulaCargaRuptura?.urlCertificadoCalibracao ||
  ensaio?.celulaCargaRuptura?.url ||
  "";
  const temCertificadoCelulaCarga = Boolean(
    !tipoSoloCimento && ensaio?.celulaCargaRuptura && (caminhoCertificado || certificadoCelulaCarga)
  );

  useEffect(() => {
    setCertificadoPronto(false);
  }, [caminhoCertificado, certificadoCelulaCarga]);


  useEffect(() => {
    if (!autoPrint || jaImprimiuRef.current || (temCertificadoCelulaCarga && !certificadoPronto)) return;

    jaImprimiuRef.current = true;
    const frame = window.requestAnimationFrame(
      tipoSoloCimento ? imprimirEmRetrato : imprimirRelatorioCeramico
    );
    return () => window.cancelAnimationFrame(frame);
  }, [autoPrint, temCertificadoCelulaCarga, certificadoPronto, tipoSoloCimento, imprimirRelatorioCeramico]);

  if (!ensaio) return null;

  if (tipoSoloCimento) {
    return (
      <RelatorioSoloCimento
        ensaio={ensaio}
        blocos={blocos}
        blocosAbsorcao={blocosAbsorcao}
        resumo={resumo}
        fotosRelatorio={fotosRelatorio}
        onVoltar={onVoltar}
      />
    );
  }

  return (
    <div className="relatorio-bloco-ceramico-print" ref={relatorioCeramicoRef}>
      {onVoltar && (
        <div className="acoes-print">
          <button className="btn-voltar" onClick={onVoltar}>
            Voltar
          </button>
          <button className="btn-imprimir" onClick={imprimirRelatorioCeramico} disabled={temCertificadoCelulaCarga && !certificadoPronto}>
            {temCertificadoCelulaCarga && !certificadoPronto ? "Carregando certificado..." : "Imprimir"}
          </button>
        </div>
      )}

      <div className="pagina">
        <Cabecalho ensaio={ensaio} dataRelatorio={dataRelatorio} />

        <div className="dados-cliente">
  <div><strong>CLIENTE:</strong> {valor(ensaio.cliente)}</div>
  <div><strong>EMPRESA SOLICITANTE:</strong> {valor(ensaio.obra || ensaio.empresaSolicitante || ensaio.cliente)}</div>
  <div><strong>ENDEREÇO:</strong> {valor(ensaio.endereco)}</div>
</div>

        <div className="secao">1. OBJETIVO DO ENSAIO</div>
        <div className="texto">
          Análise dimensional, determinação da resistência à compressão e da absorção de água em blocos de alvenaria de vedação (tijolo cerâmico).
        </div>

        <div className="secao">2. IDENTIFICAÇÃO DA AMOSTRA</div>
        <table className="tabela-info">
          <tbody>
            <tr><td>Procedência:</td><td>{valor(ensaio.procedencia || ensaio.cliente)}</td></tr>
            <tr><td>Bloco:</td><td>{valor(ensaio.blocoDescricao)}</td></tr>
            <tr>
                <td>Lote:</td>
                <td>
                  {ensaio.lote ? formatarDataBR(ensaio.lote) : "-"}
                </td>
              </tr>
            <tr><td>Dimensões (Larg x Alt x Comp) mm:</td><td>{valor(ensaio.dimensoesDeclaradas)}</td></tr>
            <tr><td>Resistência esperada:</td><td>{valor(ensaio.resistenciaEsperada)}</td></tr>
          </tbody>
        </table>

        <div className="secao">3. LOCAL DA APLICAÇÃO</div>
        <div className="texto">{valor(ensaio.localAplicacao || ensaio.obra)}</div>

        <div className="secao">4. METODOLOGIA</div>
        <div className="texto">
          Os ensaios seguiram as prescrições das normas da ABNT:
          <br />
          NBR 15270-1:2023 - Componentes cerâmicos - Blocos e tijolos para alvenaria - Parte 1: Requisitos
          <br />
          NBR 15270-2:2023 - Componentes cerâmicos - Blocos e tijolos para alvenaria - Parte 2: Métodos de ensaios
        </div>

        <div className="secao">5. DADOS DOS ENSAIOS</div>
        <div className="subsecao">5.1. Ensaio de compressão e análise dimensional</div>
        <div className="texto">
          Foram ensaiados à compressão axial: <strong>{blocos.length}</strong> blocos cerâmicos.
            Data de fabricação: <strong>{dataFabricacao}</strong>.
            Data do ensaio: <strong>{formatarDataBR(ensaio.dataEnsaio)}</strong>.
            Idade declarada: <strong>{valor(idade)}</strong> dias.
        </div>

        <div className="quadro-titulo">Quadro 1 - Resultados de Resistência à Compressão Axial e análise dimensional</div>
        <table className="tabela-resultados tabela-planilha quadro-1">
          <thead>
            <tr>
              <th rowSpan="3">BLOC<br />O<br />Nº</th>
              <th rowSpan="3">Massa<br />(g)</th>
              <th colSpan="9">DIMENSÕES (mm)</th>
              <th colSpan="2">RUPTURA</th>
            </tr>
            <tr>
              <th colSpan="2">Largura (L)</th>
              <th colSpan="2">Comprim. (C)</th>
              <th colSpan="2">Altura (H)</th>
              <th colSpan="3">Média</th>
              <th rowSpan="2">Área<br />(mm²)</th>
              <th rowSpan="2">Tensão<br />(MPa)</th>
            </tr>
            <tr>
              <th>1ª Det.</th>
              <th>2ª Det.</th>
              <th>1ª Det.</th>
              <th>2ª Det.</th>
              <th>1ª Det.</th>
              <th>2ª Det.</th>
              <th>L</th>
              <th>C</th>
              <th>H</th>
            </tr>
          </thead>
          <tbody>
            {blocos.map((item, index) => (
              <tr key={index}>
                <td>{item.bloco || index + 1}</td>
                <td>{valorTabela(item.massa, 0)}</td>
                <td>{valorTabela(item.largura1, 2)}</td>
                <td>{valorTabela(item.largura2, 2)}</td>
                <td>{valorTabela(item.comprimento1, 2)}</td>
                <td>{valorTabela(item.comprimento2, 2)}</td>
                <td>{valorTabela(item.altura1, 2)}</td>
                <td>{valorTabela(item.altura2, 2)}</td>
                <td>{valorTabela(item.larguraMedia, 1)}</td>
                <td>{valorTabela(item.comprimentoMedia, 1)}</td>
                <td>{valorTabela(item.alturaMedia, 1)}</td>
                <td>{valorTabela(item.area, 0)}</td>
                <td><strong>{valorTabela(item.tensaoRupturaMpa, 2)}</strong></td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="legenda-quadro-1">
          <strong>LEGENDA:</strong>
          <span>L= Largura</span>
          <span>C= Comprimento</span>
          <span>H= Altura</span>
        </div>
        <LinhaFinal />
      </div>

      <div className="pagina">
        <Cabecalho ensaio={ensaio} dataRelatorio={dataRelatorio} />

        <div className="quadro-titulo">Quadro 2 - Médias das análises dimensionais</div>
        <table className="tabela-resultados tabela-planilha quadro-2">
          <thead>
            <tr>
              <th rowSpan="4">BLOCO<br /><br />Nº</th>
              <th colSpan="9">DIMENSÕES NOMINAL (mm)</th>
            </tr>
            <tr>
              <th colSpan="3">Largura (L)  {valorTabela(dimensoesNominais.largura, 0)} mm</th>
              <th colSpan="3">Comprim. (C)  {valorTabela(dimensoesNominais.comprimento, 0)} mm</th>
              <th colSpan="3">Altura (H)  {valorTabela(dimensoesNominais.altura, 0)} mm</th>
            </tr>
            <tr>
              <th colSpan="3">diferença</th>
              <th colSpan="3">diferença</th>
              <th colSpan="3">diferença</th>
            </tr>
            <tr>
              <th>Individual<br />1ª Det.</th>
              <th>2ª Det.</th>
              <th>média</th>
              <th>Individual<br />1ª Det.</th>
              <th>2ª Det.</th>
              <th>média</th>
              <th>Individual<br />1ª Det.</th>
              <th>2ª Det.</th>
              <th>média</th>
            </tr>
          </thead>
          <tbody>
            {blocos.map((item, index) => (
              <tr key={index}>
                <td>{item.bloco || index + 1}</td>
                <td>{valorTabela(diferencaNominal(dimensoesNominais.largura, item.largura1), 1)}</td>
                <td>{valorTabela(diferencaNominal(dimensoesNominais.largura, item.largura2), 1)}</td>
                <td><strong>{valorTabela(diferencaNominal(dimensoesNominais.largura, item.larguraMedia), 1)}</strong></td>
                <td>{valorTabela(diferencaNominal(dimensoesNominais.comprimento, item.comprimento1), 1)}</td>
                <td>{valorTabela(diferencaNominal(dimensoesNominais.comprimento, item.comprimento2), 1)}</td>
                <td><strong>{valorTabela(diferencaNominal(dimensoesNominais.comprimento, item.comprimentoMedia), 1)}</strong></td>
                <td>{valorTabela(diferencaNominal(dimensoesNominais.altura, item.altura1), 1)}</td>
                <td>{valorTabela(diferencaNominal(dimensoesNominais.altura, item.altura2), 1)}</td>
                <td><strong>{valorTabela(diferencaNominal(dimensoesNominais.altura, item.alturaMedia), 1)}</strong></td>
              </tr>
            ))}
            <tr className="linha-media-dimensoes">
              <td colSpan="3"><strong>MÉDIA DIMENSÕES</strong></td>
              <td><strong>{valorTabela(mediaValoresAssinados(...blocos.map((item) => diferencaNominal(dimensoesNominais.largura, item.larguraMedia))), 1)}</strong></td>
              <td colSpan="2"></td>
              <td><strong>{valorTabela(mediaValoresAssinados(...blocos.map((item) => diferencaNominal(dimensoesNominais.comprimento, item.comprimentoMedia))), 1)}</strong></td>
              <td colSpan="2"></td>
              <td><strong>{valorTabela(mediaValoresAssinados(...blocos.map((item) => diferencaNominal(dimensoesNominais.altura, item.alturaMedia))), 1)}</strong></td>
            </tr>
          </tbody>
        </table>

        <table className="tabela-dimensoes-requisitos">
          <tbody>
            <tr>
              <td rowSpan="4" className="celula-foto-dimensional">
                {ensaio.fotoAnaliseDimensional?.url ? (
                  <img src={ensaio.fotoAnaliseDimensional.url} alt="Análise dimensional" />
                ) : (
                  <div className="foto-dimensional-vazia">-</div>
                )}
              </td>
              <td>Tolerância individual admissível para as dimensões</td>
              <td>(mm)</td>
              <td><strong>5,0</strong></td>
            </tr>
            <tr>
              <td>Tolerância adm. em relação à média das dimensões</td>
              <td>(mm)</td>
              <td><strong>3,0</strong></td>
            </tr>
            <tr>
              <td>Valor mínimo</td>
              <td>(MPa)</td>
              <td><strong>{valorTabela(resumo.resistenciaMinima, 2)}</strong></td>
            </tr>
            <tr>
              <td>Resistência média</td>
              <td>(MPa)</td>
              <td><strong>{valorTabela(resumo.resistenciaMedia, 1)}</strong></td>
            </tr>
          </tbody>
        </table>

        <div className="subsecao">5.2. Ensaio de absorção de água</div>
        <div className="quadro-titulo">Quadro 3 - Resultados ensaio de Absorção</div>
        <table className="tabela-resultados">
          <thead>
            <tr>
              <th>Bloco Nº</th>
              <th>Massa seca</th>
              <th>Massa úmida</th>
              <th>Absorção (%)</th>
              <th>IMAGEM REPRESENTATIVA DO ENSAIO</th>
            </tr>
          </thead>
          <tbody>
            {blocosAbsorcao.map((item, index) => (
              <tr key={index}>
                <td>{item.bloco || index + 1}</td>
                <td>{valorTabela(item.massaSeca, 1)}</td>
                <td>{valorTabela(item.massaUmida, 1)}</td>
                <td><strong>{valorTabela(item.absorcao, 1)}</strong></td>
                {index === 0 && (
                  <td rowSpan={Math.max(blocosAbsorcao.length, 1) + 1} className="celula-fotos-absorcao">
                    <div className="fotos-absorcao">
                      {[0, 1].map((fotoIndex) => {
                        const foto = ensaio.absorcaoFotos?.[fotoIndex];
                        return foto?.url ? (
                          <img key={fotoIndex} src={foto.url} alt={foto.legenda || `Absorção ${fotoIndex + 1}`} />
                        ) : (
                          <div key={fotoIndex} className="foto-absorcao-vazia">-</div>
                        );
                      })}
                    </div>
                  </td>
                )}
              </tr>
            ))}
            {!blocosAbsorcao.length && (
              <tr>
                <td colSpan="4">-</td>
                <td rowSpan="2" className="celula-fotos-absorcao">
                  <div className="fotos-absorcao">
                    {[0, 1].map((fotoIndex) => {
                      const foto = ensaio.absorcaoFotos?.[fotoIndex];
                      return foto?.url ? (
                        <img key={fotoIndex} src={foto.url} alt={foto.legenda || `Absorção ${fotoIndex + 1}`} />
                      ) : (
                        <div key={fotoIndex} className="foto-absorcao-vazia">-</div>
                      );
                    })}
                  </div>
                </td>
              </tr>
            )}
            <tr className="linha-absorcao-media">
              <td colSpan="2">Absorção média (%)</td>
              <td colSpan="2"><strong>{valorTabela(resumo.absorcaoMedia, 1)}</strong></td>
            </tr>
          </tbody>
        </table>
        <LinhaFinal />
      </div>

      <div className="pagina">
        <Cabecalho ensaio={ensaio} dataRelatorio={dataRelatorio} />

        <div className="subsecao">5.3. Determinação da espessura das paredes externas e septos.</div>
        <div className="quadro-titulo">Quadro 4 - Análise dimensional das paredes externas e septos</div>
        <table className="tabela-resultados tabela-planilha quadro-4">
          <thead>
            <tr>
              <th rowSpan="4">BLOCO<br /><br />Nº</th>
              <th colSpan="9">DIMENSÕES</th>
              <th rowSpan="4">Desvio em<br />Relação ao<br />Esquadro (mm)</th>
              <th rowSpan="4">Det. Planeza<br />das Faces<br />(mm)</th>
            </tr>
            <tr>
              <th colSpan="4">SÉPTOS</th>
              <th colSpan="5">PAREDES EXTERNAS</th>
            </tr>
            <tr>
              <th colSpan="4">Determinações</th>
              <th colSpan="5">Determinações</th>
            </tr>
            <tr>
              <th>1ª</th>
              <th>2ª</th>
              <th>3ª</th>
              <th>4ª</th>
              <th>1ª</th>
              <th>2ª</th>
              <th>3ª</th>
              <th>4ª</th>
              <th>soma<br />paredes</th>
            </tr>
          </thead>
          <tbody>
            {blocos.map((item, index) => (
              <tr key={index}>
                <td>{item.bloco || index + 1}</td>
                <td>{valorTabela(item.septo1, 2)}</td>
                <td>{valorTabela(item.septo2, 2)}</td>
                <td>{valorTabela(item.septo3, 2)}</td>
                <td>{valorTabela(item.septo4, 2)}</td>
                <td>{valorTabela(item.parede1, 2)}</td>
                <td>{valorTabela(item.parede2, 2)}</td>
                <td>{valorTabela(item.parede3, 2)}</td>
                <td>{valorTabela(item.parede4, 2)}</td>
                <td>{valorTabela(item.somaParedes, 1)}</td>
                <td>{valorTabela(item.desvioRelacaoEsquadro, 1)}</td>
                <td>{valorTabela(item.planezaFaces, 1)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="quadro-titulo">Quadro 5 - Requisitos mínimos conforme NBR 15270-1</div>
        <table className="tabela-resultados requisitos quadro-5">
          <thead>
            <tr>
              <th>Tipo</th>
              <th colSpan="2">Espessura das paredes<br />mm</th>
              <th colSpan="2">Dimensões do<br />bloco mm</th>
              <th>Absorção %</th>
              <th>fb mínimo<br />MPa</th>
              <th>Desvio em<br />Relação ao<br />Esquadro<br />mm</th>
              <th>Planeza<br />das<br />faces</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{valor(ensaio.tipoVedacao, "VED15")}</td>
              <td>soma das<br />paredes ≥ 20</td>
              <td>paredes<br />indiv. ≥ 7</td>
              <td>individual<br />± 5</td>
              <td>média<br />± 3</td>
              <td>8 a 25</td>
              <td>{valor(ensaio.fbMinimo, "1,5")}</td>
              <td>máximo<br />3 mm</td>
              <td>máximo<br />3 mm</td>
            </tr>
          </tbody>
        </table>

        <div className="secao">6. ANEXOS</div>
        <div className="texto">ANEXO 1 - Relatório fotográfico;</div>

        
        {ensaio.celulaCargaRuptura && temCertificadoCelulaCarga ? (
  <div className="texto">ANEXO 2 - Certificado de Calibração - célula de carga;</div>
) : ensaio.prensaRuptura ? (
  <div className="texto">ANEXO 2 - Certificado de Calibração - prensa de concreto;</div>
) : null}
        <div className="secao">Observações:</div>
        <div className="texto">{valor(ensaio.observacao)}</div>
        <div className="assinatura">
  <img src="/Images/assinatura.png" alt="Assinatura" />
  <div className="linha-assinatura" />
  <strong>José Carlos Lemes Leal</strong>
  <strong>Eng. Civil Esp. CREA 203379/D-TO</strong>
</div>
        <LinhaFinal />
      </div>

      <RelatorioFotografico ensaio={ensaio} fotos={fotosRelatorio} />

      {ensaio.celulaCargaRuptura ? (
  temCertificadoCelulaCarga ? (
    <PaginasCertificadoCelulaCarga
      ensaio={ensaio}
      dataRelatorio={dataRelatorio}
      caminho={caminhoCertificado}
      url={certificadoCelulaCarga}
      onPronto={() => setCertificadoPronto(true)}
    />
  ) : (
    <div className="certificado-prensa-anexo">
      <div className="pagina">
        <Cabecalho ensaio={ensaio} dataRelatorio={dataRelatorio} />
        <div className="secao">ANEXO 2 - CERTIFICADO DE CALIBRAÇÃO - CÉLULA DE CARGA</div>
        <div className="texto">Certificado de calibração não localizado no cadastro do equipamento selecionado.</div>
      </div>
    </div>
  )
) : ensaio.prensaRuptura ? (
  <div className="certificado-prensa-anexo">
    <RelatorioCalibracaoPrensaPrint dados={ensaio.prensaRuptura} />
  </div>
) : null}
    </div>
  );
}

function mediaValores(...valores) {
  const lista = valores
    .map((valorCampo) => Number(String(valorCampo || "").replace(",", ".")))
    .filter((valorCampo) => Number.isFinite(valorCampo) && valorCampo > 0);
  if (!lista.length) return "";
  return lista.reduce((total, valorCampo) => total + valorCampo, 0) / lista.length;
}

function mediaValoresAssinados(...valores) {
  const lista = valores
    .map((valorCampo) => Number(String(valorCampo ?? "").replace(",", ".")))
    .filter((valorCampo) => Number.isFinite(valorCampo));
  if (!lista.length) return "";
  return lista.reduce((total, valorCampo) => total + valorCampo, 0) / lista.length;
}
